import './styles.scss';

import { useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Text, Button, StatusChip } from '@fragments';
import TabsSelector from '@components/TabsSelector';
import { UserAvatar } from '@components/Community';
import * as Material from '@mui/material';

import MentoringReviewBar from './components/MentoringReviewBar';

import {
    IconArrowUpRight,
    IconArrowLeft,
    IconAlphabetLatin,
    IconUser,
    IconMail,
    IconPhone,
    IconHash,
    IconLink,
    IconChevronDown,
    IconListCheck,
    IconToggleRight,
    IconGauge,
    IconCalendar,
    IconCalendarTime,
    IconCoin,
    IconBarbell,
    IconGenderBigender,
    IconRun,
    IconClipboardCheck,
    IconClipboardList,
    IconChevronLeft,
    IconChevronRight,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

dayjs.locale('pt-br');

const QUESTION_TYPE_ICONS = {
    text: IconAlphabetLatin,
    full_name: IconUser,
    email: IconMail,
    phone: IconPhone,
    number: IconHash,
    url: IconLink,
    select: IconChevronDown,
    choices: IconListCheck,
    yes_no_maybe: IconToggleRight,
    scale: IconGauge,
    date: IconCalendar,
    datetime: IconCalendarTime,
    monetary: IconCoin,
    weight: IconBarbell,
    gender: IconGenderBigender,
    body_structure: IconRun,
};

const GENDER_LABELS = {
    male: 'Masculino',
    female: 'Feminino',
    other: 'Outro',
    prefer_not_to_say: 'Prefiro não dizer',
};

const BODY_STRUCTURE_LABELS = {
    ectomorph: 'Ectomorfo',
    mesomorph: 'Mesomorfo',
    endomorph: 'Endomorfo',
};

function resolveChoiceLabels(value, settings) {
    const options = settings?.options ?? [];
    const ids = Array.isArray(value) ? value : [value];
    return ids.map(id => options.find(o => o.id === id)?.label ?? id);
}

function AnswerValue({ entry }) {
    const { question, value } = entry;
    const { type, settings } = question ?? {};

    if (value === null || value === undefined || value === '') {
        return <Text.span contrast={4} className="answer-empty">-</Text.span>;
    }

    if (type === 'choices') {
        const labels = resolveChoiceLabels(value, settings);
        return (
            <div className="answer-choices">
                {labels.map((label, i) => (
                    <span key={i} className="choice-chip">{label}</span>
                ))}
            </div>
        );
    }

    if (type === 'yes_no_maybe') {
        const labels = { yes: 'Sim', no: 'Não', maybe: 'Talvez' };
        return <span className="choice-chip">{labels[value] ?? value}</span>
    }

    if (type === 'gender') {
        return <Text.span contrast={8} className="answer-text">{GENDER_LABELS[value] ?? value}</Text.span>;
    }

    if (type === 'body_structure') {
        return <Text.span contrast={8} className="answer-text">{BODY_STRUCTURE_LABELS[value] ?? value}</Text.span>;
    }

    if (type === 'scale') {
        const max = settings?.max ?? 10;
        const minLabel = settings?.minLabel;
        const maxLabel = settings?.maxLabel;
        return (
            <div className="answer-scale">
                <Text.h4 contrast={8}>{value}</Text.h4>
                <Text.span contrast={5}>/{max}</Text.span>
            </div>
        );
    }

    if (type === 'weight') {
        return <Text.span contrast={8} className="answer-text">{value} kg</Text.span>;
    }

    if (type === 'monetary') {
        const num = Number(value);
        if (!isNaN(num)) {
            return <Text.span contrast={8} className="answer-text">{num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</Text.span>;
        }
    }

    if (type === 'date') return <Text.span contrast={8} className="answer-text">{dayjs(value).format('DD/MM/YYYY')}</Text.span>;
    if (type === 'datetime') return <Text.span contrast={8} className="answer-text">{dayjs(value).format('DD/MM/YYYY HH:mm')}</Text.span>;

    return <Text.span contrast={8} className="answer-text">{String(value)}</Text.span>;
}

function QuestionAnswerRow({ entry, index }) {
    const { question } = entry;
    const type = question?.type;
    const Icon = QUESTION_TYPE_ICONS[type] ?? IconAlphabetLatin;

    return (
        <div className="qa-row" style={{ animationDelay: `${index * 40}ms` }}>
            <div className="qa-question-label">
                <Text.h6 left={<Icon />} contrast={5}>{question?.label ?? `Pergunta ${index + 1}`}</Text.h6>
            </div>
            <div className="qa-answer">
                <AnswerValue entry={entry} />
            </div>
        </div>
    );
}

function StepBlock({ stepEntry, globalOffset }) {
    const { step, answers } = stepEntry;
    return (
        <div className="step-block pb-3">
            {step?.title && (
                <div className="step-block__header">
                    <Text.h3 weight={400} contrast={9} className="step-block__label mb-2">
                        {step.title}
                    </Text.h3>
                </div>
            )}
            <div className="answers-list">
                {answers.map((entry, i) => (
                    <QuestionAnswerRow
                        key={entry.question?.id ?? i}
                        entry={entry}
                        index={globalOffset + i}
                    />
                ))}
            </div>
        </div>
    );
}

function SubmissionListItem({ submission, selected, onClick }) {
    const date = submission.completed_at ? dayjs(submission.completed_at) : null;
    const formattedDate = date
        ? date.format('D [de] MMM, YYYY').replace(/ de ([a-z])/, (_, l) => ` de ${l.toUpperCase()}`)
        : '—';

    return (
        <button
            className={`submission-list-item${selected ? ' submission-list-item--active' : ''}`}
            onClick={() => onClick(submission)}
        >
            <span className={`sli-radio${selected ? ' sli-radio--active' : ''}`} />
            <div className="submission-list-item__date">
                <Text.h6 contrast={selected ? 9 : 6} weight={400}>
                    {formattedDate}
                </Text.h6>
                <Text.span contrast={selected ? 6 : 4}>
                    {date ? date.format('HH:mm:ss') : ''}
                </Text.span>
            </div>
            {!submission.completed && <span className="submission-list-item__pending-dot" />}
        </button>
    );
}

function OtherSubmissionRow({ submission, slug }) {
    const navigate = useNavigate();
    const date = submission.completed_at
        ? (() => {
            const d = dayjs(submission.completed_at);
            const month = d.format('MMM');
            const capitalizedMonth = month.charAt(0).toUpperCase() + month.slice(1);
            return d.format(`D [de] [${capitalizedMonth}] [de] YYYY [às] HH:mm`);
        })()
        : null;

    function handleClick() {
        navigate(`/${slug}/settings/forms/submissions/${submission.id}?form_id=${submission.form_id}`);
    }

    return (
        <button className="other-sub-row" onClick={handleClick}>
            <div className="other-sub-row__info">
                <Text.span contrast={7} left={<IconClipboardCheck />}>
                    {submission.form?.title ?? '—'}
                </Text.span>
                <Text.span contrast={4} className="other-sub-row__date">{date ?? '—'}</Text.span>
            </div>
            {!submission.completed && <StatusChip.Alert text="Em andamento" />}
        </button>
    );
}

function AnswersSkeleton() {
    return (
        <div className="answers-skeleton">
            {[1, 2, 3, 4].map(i => (
                <div key={i} className="qa-row qa-row--skeleton">
                    <Material.Skeleton variant="text" width="40%" height={16} />
                    <Material.Skeleton variant="text" width="75%" height={20} />
                </div>
            ))}
        </div>
    );
}

function getScrollContainer() {
    return document.querySelector('.config-content');
}

export default function SubmissionDetailsView({
    slug,
    submission,
    userDisplay,
    formInfo,
    loadingSubmission,
    history,
    loadingHistory,
    selectedSubmissionId,
    selectedSteps,
    loadingAnswers,
    otherSubmissions,
    loadingOther,
    otherPage,
    otherPages,
    otherTotal,
    activeTab,
    setActiveTab,
    handleSelectSubmission,
    handleOtherPage,
}) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Mentoring context — set when this page is opened from an enrollment's
    // "Visualizar aplicação". `return_to` brings the reviewer back; `mentoring`
    // carries the enrollment status ("pending" enables approve/reject).
    const returnTo = searchParams.get('return_to');
    const mentoringStatus = searchParams.get('mentoring');
    const reviewPhone = searchParams.get('phone');
    const mentoringMode = Boolean(mentoringStatus);
    const canReview = mentoringStatus === 'pending';

    // Sentinel sits immediately before the sticky tabs in normal flow (not sticky itself),
    // so getBoundingClientRect always returns its true visual position — usable from any scroll depth.
    const tabsSentinelRef = useRef(null);
    // Set to true when a history item is clicked; cleared after the scroll fires.
    const pendingScrollRef = useRef(false);
    const historyCount = history.length;

    // Scroll 100px down when the details page first loads
    useEffect(() => {
        const container = getScrollContainer();
        if (container) {
            container.scrollTo({ top: 120, behavior: 'smooth' });
        }
    }, []);

    // Fire the scroll AFTER loadingAnswers drops back to false — at that point the full answers
    // are in the DOM and the page has its real scrollable height, so scrollTo can reach the target.
    useEffect(() => {
        if (!loadingAnswers && pendingScrollRef.current) {
            pendingScrollRef.current = false;
            scrollToTabs();
        }
    }, [loadingAnswers]);

    function scrollToTabs() {
        const container = getScrollContainer();
        if (!container || !tabsSentinelRef.current) return;

        const subheader = container.querySelector('.config-subheader');
        const subheaderHeight = subheader ? subheader.offsetHeight : 0;

        const containerRect = container.getBoundingClientRect();
        const sentinelRect = tabsSentinelRef.current.getBoundingClientRect();
        const target = container.scrollTop + (sentinelRect.top - containerRect.top) - subheaderHeight;
        container.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
    }

    function handleSelectWithScroll(sub) {
        pendingScrollRef.current = true;
        handleSelectSubmission(sub);
    }

    // Count total questions across all steps for animation offset
    let globalOffset = 0;

    const tabs = [
        { key: 'history', label: 'Histórico' },
        { key: 'comparison', label: 'Comparação', badge: 'Em breve', disabled: true },
    ];

    function handleGoToMember() {
        if (submission?.user?.id) navigate(`/user/${submission.user.id}`);
    }

    return (
        <div className={`sub-details px-4 pb-6${mentoringMode ? ' sub-details--with-review-bar' : ''}`}>
            <div className="sub-details__layout">

                {/* ── Main column ── */}
                <div className="sub-details__main">

                    {/* Back to list */}
                    <div className="sub-details__back-row">
                        <Button
                            size="small"
                            color="inverse"
                            variant="text"
                            startIcon={<IconArrowLeft size={14} />}
                            onClick={() => navigate(returnTo || `/${slug}/settings/forms/submissions`)}
                            preventLoading
                        >
                            {returnTo ? 'Voltar' : 'Voltar para lista'}
                        </Button>
                    </div>

                    {/* User info */}
                    <div className="sub-details__user-row">
                        <UserAvatar user={userDisplay ?? {}} size={52} loading={loadingSubmission} />
                        <div className="sub-details__user-info">
                            {loadingSubmission ? (
                                <>
                                    <Material.Skeleton variant="text" width={200} height={22} />
                                    <Material.Skeleton variant="text" width={140} height={16} />
                                </>
                            ) : (
                                <>
                                    <Text.h5 contrast={8}>{userDisplay?.username ?? '—'}</Text.h5>
                                    <Text.span contrast={5}>{userDisplay?.email ?? '—'}</Text.span>
                                </>
                            )}
                        </div>
                        {submission?.user?.id && !loadingSubmission && (
                            <Button
                                size="small"
                                color="inverse"
                                variant="rounded"
                                startIcon={<IconArrowUpRight />}
                                onClick={handleGoToMember}
                                tooltip="Ver perfil do membro"
                                preventLoading
                            >
                                Ver perfil
                            </Button>
                        )}
                    </div>

                    {/* Form title + count */}
                    <div className="sub-details__form-title mt-5">
                        {loadingSubmission && !formInfo ? (
                            <>
                                <Material.Skeleton variant="text" width={380} height={34} />
                                <Material.Skeleton variant="text" width={160} height={18} />
                            </>
                        ) : (
                            <>
                                <Text.h3 contrast={9}>
                                    Submissões no formulário "{formInfo?.title ?? '—'}"
                                </Text.h3>
                                <Text.span contrast={5}>
                                    {loadingHistory
                                        ? 'Carregando...'
                                        : `${historyCount} submiss${historyCount !== 1 ? 'ões' : 'ão'} encontrada${historyCount !== 1 ? 's' : ''}`}
                                </Text.span>
                            </>
                        )}
                    </div>

                    {/* Sentinel: non-sticky marker used to measure the tabs' true position */}
                    <div ref={tabsSentinelRef} style={{ height: 0 }} aria-hidden="true" />

                    {/* Tabs */}
                    <div className="sub-details__tabs-sticky">
                        <TabsSelector
                            tabs={tabs}
                            value={activeTab}
                            onChange={setActiveTab}
                            className="sub-details__tabs"
                        />
                        <Material.Divider className="mb-0" />
                    </div>

                    {/* History tab content */}
                    {activeTab === 'history' && (
                        <div className="sub-details__history">

                            {/* Left: submission list */}
                            <div className="sub-details__list-col">
                                <div className="sub-details__list-sticky">
                                    {loadingHistory ? (
                                        [1, 2, 3].map(i => (
                                            <Material.Skeleton
                                                key={i}
                                                variant="rectangular"
                                                height={62}
                                                style={{ borderRadius: 'var(--gap-1)', marginBottom: 'var(--gap-1)' }}
                                            />
                                        ))
                                    ) : history.length === 0 ? (
                                        <div className="sub-details__list-empty">
                                            <IconClipboardList size={28} stroke={1.2} />
                                            <Text.span contrast={4}>Nenhuma submissão</Text.span>
                                        </div>
                                    ) : (
                                        history.map(sub => (
                                            <SubmissionListItem
                                                key={sub.id}
                                                submission={sub}
                                                selected={sub.id === selectedSubmissionId}
                                                onClick={handleSelectWithScroll}
                                            />
                                        ))
                                    )}
                                </div>
                            </div>

                            {/* Right: answers panel */}
                            <div className="sub-details__answers-col">
                                {loadingAnswers ? (
                                    <AnswersSkeleton />
                                ) : selectedSteps.length === 0 ? (
                                    <div className="sub-details__answers-empty">
                                        <IconClipboardCheck size={36} stroke={1} />
                                        <Text.h5 contrast={5}>Sem respostas</Text.h5>
                                        <Text.span contrast={3}>
                                            Selecione uma submissão para ver as respostas
                                        </Text.span>
                                    </div>
                                ) : (
                                    <div className="steps-list">
                                        {selectedSteps.map((stepEntry) => {
                                            const block = (
                                                <StepBlock
                                                    key={stepEntry.step?.id}
                                                    stepEntry={stepEntry}
                                                    globalOffset={globalOffset}
                                                />
                                            );
                                            globalOffset += stepEntry.answers?.length ?? 0;
                                            return block;
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Right sidebar ── */}
                <aside className="sub-details__sidebar mt-5">
                    <div className="sub-details__sidebar-header">
                        <Text.h4 contrast={6}>
                            Outras respostas do usuário na comunidade
                        </Text.h4>
                        {!loadingOther && otherTotal > 0 && (
                            <span className="sidebar-count">{otherTotal}</span>
                        )}
                    </div>

                    <div className="sub-details__sidebar-list">
                        {loadingOther ? (
                            [1, 2, 3, 4].map(i => (
                                <div key={i} className="other-sub-skeleton">
                                    <Material.Skeleton variant="text" width="70%" height={16} />
                                    <Material.Skeleton variant="text" width="45%" height={14} />
                                </div>
                            ))
                        ) : otherSubmissions.length === 0 ? (
                            <div className="sidebar-empty">
                                <Text.span contrast={4}>Nenhuma outra resposta</Text.span>
                            </div>
                        ) : (
                            otherSubmissions.map(sub => (
                                <OtherSubmissionRow key={sub.id} submission={sub} slug={slug} />
                            ))
                        )}
                    </div>

                    {otherPages > 1 && (
                        <div className="sub-details__sidebar-pagination">
                            <Button
                                icon={<IconChevronLeft size={14} />}
                                size="micro"
                                color="inverse"
                                variant="rounded"
                                disabled={otherPage <= 1}
                                onClick={() => handleOtherPage(otherPage - 1)}
                                preventLoading
                            />
                            <Text.span contrast={5}>{otherPage} / {otherPages}</Text.span>
                            <Button
                                icon={<IconChevronRight size={14} />}
                                size="micro"
                                color="inverse"
                                variant="rounded"
                                disabled={otherPage >= otherPages}
                                onClick={() => handleOtherPage(otherPage + 1)}
                                preventLoading
                            />
                        </div>
                    )}
                </aside>
            </div>

            {mentoringMode && (
                <MentoringReviewBar
                    returnTo={returnTo}
                    canReview={canReview}
                    phone={reviewPhone}
                />
            )}
        </div>
    );
}
